/**
 * Разреженное состояние листа: только непустые ячейки и стили.
 */
import {
  cellKey,
  colLabel,
  parseCellRef,
  shiftFormulaRefs,
} from './formulaEngine.js';

/**
 * Заливка: сразу три палитры — 1 Excel-пастель, 2 midtone, 4 яркий маркер.
 * group — разделитель в тулбаре.
 */
export const FILL_PRESETS = [
  { id: 'none', label: 'Без заливки', color: null },
  { id: 'p-blue', label: 'Пастель синий', color: '#DDEBF7', group: 'pastel' },
  { id: 'p-green', label: 'Пастель зелёный', color: '#E2EFDA', group: 'pastel' },
  { id: 'p-yellow', label: 'Пастель жёлтый', color: '#FFF2CC', group: 'pastel' },
  { id: 'p-orange', label: 'Пастель оранжевый', color: '#FCE4D6', group: 'pastel' },
  { id: 'p-lilac', label: 'Пастель сиреневый', color: '#E2D5F1', group: 'pastel' },
  { id: 'p-mint', label: 'Пастель мятный', color: '#D9EAD3', group: 'pastel' },
  { id: 'p-pink', label: 'Пастель розовый', color: '#F4CCCC', group: 'pastel' },
  { id: 'm-slate', label: 'Синий', color: '#5B7C99', group: 'mid' },
  { id: 'm-sage', label: 'Зелёный', color: '#6A8F71', group: 'mid' },
  { id: 'm-gold', label: 'Горчичный', color: '#C4A35A', group: 'mid' },
  { id: 'm-terra', label: 'Терракота', color: '#B87A6B', group: 'mid' },
  { id: 'm-plum', label: 'Фиолетовый', color: '#8B7AA8', group: 'mid' },
  { id: 'm-teal', label: 'Бирюзовый', color: '#6E8B8B', group: 'mid' },
  { id: 'm-clay', label: 'Коричневый', color: '#A67C6D', group: 'mid' },
  { id: 'b-sky', label: 'Маркер голубой', color: '#7EB6D9', group: 'bright' },
  { id: 'b-lime', label: 'Маркер зелёный', color: '#8FCB8F', group: 'bright' },
  { id: 'b-yellow', label: 'Маркер жёлтый', color: '#F0D060', group: 'bright' },
  { id: 'b-coral', label: 'Маркер коралл', color: '#F0A07A', group: 'bright' },
  { id: 'b-lilac', label: 'Маркер сирень', color: '#C9A0DC', group: 'bright' },
  { id: 'b-aqua', label: 'Маркер аква', color: '#7EC8C8', group: 'bright' },
  { id: 'b-salmon', label: 'Маркер лосось', color: '#F08080', group: 'bright' },
  { id: 'wall', label: 'Запрет', color: '#334155', group: 'wall' },
];

export const VIRTUAL_ROW_THRESHOLD = 80;
/** Запас под две таблицы рядом (ЕГЭ 18) + пустые строки/столбцы. */
export const DEFAULT_ROWS_18 = 50;
export const DEFAULT_COLS_18 = 50;
export const MAX_FULL_ROWS = 50;
export const MAX_FULL_COLS = 50;
export const DEFAULT_COL_WIDTH_PX = 72;
export const MIN_COL_WIDTH_PX = 32;
export const MAX_COL_WIDTH_PX = 480;

export function emptySpreadsheetState(rows = DEFAULT_ROWS_18, cols = DEFAULT_COLS_18) {
  return {
    version: 2,
    rows: Math.max(1, Number(rows) || DEFAULT_ROWS_18),
    cols: Math.max(1, Number(cols) || DEFAULT_COLS_18),
    cells: {},
    fills: {},
    walls: {},
    colWidths: {},
    aligns: {},
    filter: { enabled: false, columns: {} },
  };
}

/** Старый плотный cells → разреженный; добавить fills/walls. */
export function normalizeSpreadsheetState(raw, fallback) {
  const base = fallback || emptySpreadsheetState();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  let rows = Number(raw.rows) > 0 ? Number(raw.rows) : base.rows;
  let cols = Number(raw.cols) > 0 ? Number(raw.cols) : base.cols;
  const cells = {};
  if (raw.cells && typeof raw.cells === 'object') {
    for (const [k, v] of Object.entries(raw.cells)) {
      if (v == null) continue;
      const s = String(v);
      if (s === '') continue;
      cells[k] = s;
    }
  }
  const fills = {};
  if (raw.fills && typeof raw.fills === 'object') {
    for (const [k, v] of Object.entries(raw.fills)) {
      if (v) fills[k] = String(v);
    }
  }
  const walls = {};
  if (raw.walls && typeof raw.walls === 'object') {
    for (const [k, v] of Object.entries(raw.walls)) {
      if (v && typeof v === 'object') {
        const w = {};
        if (v.t) w.t = 1;
        if (v.r) w.r = 1;
        if (v.b) w.b = 1;
        if (v.l) w.l = 1;
        if (w.t || w.r || w.b || w.l) walls[k] = w;
      }
    }
  }
  // Запас под вторую таблицу (ЕГЭ 18). Пустые клетки не хранятся.
  const wantRows = Math.max(base.rows || 0, DEFAULT_ROWS_18);
  const wantCols = Math.max(base.cols || 0, DEFAULT_COLS_18);
  rows = Math.max(rows, wantRows);
  cols = Math.max(cols, wantCols);

  const colWidths = {};
  if (raw.colWidths && typeof raw.colWidths === 'object') {
    for (const [k, v] of Object.entries(raw.colWidths)) {
      const px = Number(v);
      if (Number.isFinite(px) && px >= MIN_COL_WIDTH_PX) {
        colWidths[String(k)] = Math.min(MAX_COL_WIDTH_PX, Math.round(px));
      }
    }
  }
  const aligns = {};
  if (raw.aligns && typeof raw.aligns === 'object') {
    for (const [k, v] of Object.entries(raw.aligns)) {
      const a = String(v || '');
      if (a === 'left' || a === 'center' || a === 'right') aligns[k] = a;
    }
  }

  let filter = { enabled: false, columns: {} };
  if (raw.filter && typeof raw.filter === 'object') {
    filter = {
      enabled: Boolean(raw.filter.enabled),
      columns: raw.filter.columns && typeof raw.filter.columns === 'object'
        ? raw.filter.columns
        : {},
    };
  }

  return {
    version: 2,
    rows,
    cols,
    cells,
    fills,
    walls,
    colWidths,
    aligns,
    filter,
  };
}

export function shouldVirtualize(state, config) {
  if (config?.virtual === true) return true;
  if (config?.virtual === false) return false;
  const rows = state?.rows || 0;
  return rows > VIRTUAL_ROW_THRESHOLD;
}

export function setCellValue(state, r, c, value) {
  const key = cellKey(r, c);
  const cells = { ...(state.cells || {}) };
  const s = value == null ? '' : String(value);
  if (s === '') delete cells[key];
  else cells[key] = s;
  return { ...state, cells };
}

export function setCellsValues(state, updates) {
  const cells = { ...(state.cells || {}) };
  for (const { row, col, value } of updates) {
    const key = cellKey(row, col);
    const s = value == null ? '' : String(value);
    if (s === '') delete cells[key];
    else cells[key] = s;
  }
  return { ...state, cells };
}

export function getFill(state, r, c) {
  return state?.fills?.[cellKey(r, c)] || null;
}

export function getWalls(state, r, c) {
  return state?.walls?.[cellKey(r, c)] || null;
}

export function getAlign(state, r, c) {
  return state?.aligns?.[cellKey(r, c)] || null;
}

export function getColWidth(state, c) {
  const w = Number(state?.colWidths?.[String(c)]);
  if (Number.isFinite(w) && w >= MIN_COL_WIDTH_PX) {
    return Math.min(MAX_COL_WIDTH_PX, w);
  }
  return DEFAULT_COL_WIDTH_PX;
}

export function setColWidths(state, colIndexes, px) {
  const width = Math.min(
    MAX_COL_WIDTH_PX,
    Math.max(MIN_COL_WIDTH_PX, Math.round(Number(px) || DEFAULT_COL_WIDTH_PX)),
  );
  const colWidths = { ...(state.colWidths || {}) };
  for (const c of colIndexes) {
    if (width === DEFAULT_COL_WIDTH_PX) delete colWidths[String(c)];
    else colWidths[String(c)] = width;
  }
  return { ...state, colWidths };
}

export function applyAlignToSelection(state, sel, align) {
  if (!sel) return state;
  const aligns = { ...(state.aligns || {}) };
  const r0 = Math.min(sel.r0, sel.r1);
  const r1 = Math.max(sel.r0, sel.r1);
  const c0 = Math.min(sel.c0, sel.c1);
  const c1 = Math.max(sel.c0, sel.c1);
  const ok = align === 'left' || align === 'center' || align === 'right';
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      const key = cellKey(r, c);
      if (!ok || align === 'left') delete aligns[key];
      else aligns[key] = align;
    }
  }
  return { ...state, aligns };
}

export function applyFillToSelection(state, sel, color) {
  if (!sel) return state;
  const fills = { ...(state.fills || {}) };
  const r0 = Math.min(sel.r0, sel.r1);
  const r1 = Math.max(sel.r0, sel.r1);
  const c0 = Math.min(sel.c0, sel.c1);
  const c1 = Math.max(sel.c0, sel.c1);
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      const key = cellKey(r, c);
      if (!color) delete fills[key];
      else fills[key] = color;
    }
  }
  return { ...state, fills };
}

/** Толстые границы на внешнем периметре выделения (стены для ЕГЭ-18). */
export function applyWallsToSelection(state, sel, sides = { t: 1, r: 1, b: 1, l: 1 }) {
  if (!sel) return state;
  const walls = { ...(state.walls || {}) };
  const r0 = Math.min(sel.r0, sel.r1);
  const r1 = Math.max(sel.r0, sel.r1);
  const c0 = Math.min(sel.c0, sel.c1);
  const c1 = Math.max(sel.c0, sel.c1);

  const bump = (r, c, edge) => {
    const key = cellKey(r, c);
    const cur = { ...(walls[key] || {}) };
    if (sides[edge]) cur[edge] = 1;
    else delete cur[edge];
    if (cur.t || cur.r || cur.b || cur.l) walls[key] = cur;
    else delete walls[key];
  };

  for (let c = c0; c <= c1; c += 1) {
    if (sides.t) bump(r0, c, 't');
    if (sides.b) bump(r1, c, 'b');
  }
  for (let r = r0; r <= r1; r += 1) {
    if (sides.l) bump(r, c0, 'l');
    if (sides.r) bump(r, c1, 'r');
  }
  return { ...state, walls };
}

/**
 * Снять стены. Без sides — все стены во всех клетках выделения.
 * С sides — только выбранные рёбра на периметре (как applyWallsToSelection).
 */
export function clearWallsInSelection(state, sel, sides = null) {
  if (!sel) return state;
  const walls = { ...(state.walls || {}) };
  const r0 = Math.min(sel.r0, sel.r1);
  const r1 = Math.max(sel.r0, sel.r1);
  const c0 = Math.min(sel.c0, sel.c1);
  const c1 = Math.max(sel.c0, sel.c1);

  if (!sides) {
    for (let r = r0; r <= r1; r += 1) {
      for (let c = c0; c <= c1; c += 1) {
        delete walls[cellKey(r, c)];
      }
    }
    return { ...state, walls };
  }

  const drop = (r, c, edge) => {
    const key = cellKey(r, c);
    const cur = { ...(walls[key] || {}) };
    delete cur[edge];
    if (cur.t || cur.r || cur.b || cur.l) walls[key] = cur;
    else delete walls[key];
  };

  for (let c = c0; c <= c1; c += 1) {
    if (sides.t) drop(r0, c, 't');
    if (sides.b) drop(r1, c, 'b');
  }
  for (let r = r0; r <= r1; r += 1) {
    if (sides.l) drop(r, c0, 'l');
    if (sides.r) drop(r, c1, 'r');
  }
  return { ...state, walls };
}

export function copyFormat(state, from, toSel) {
  if (!from || !toSel) return state;
  const fill = state.fills?.[cellKey(from.row, from.col)] || null;
  const wall = state.walls?.[cellKey(from.row, from.col)] || null;
  const align = state.aligns?.[cellKey(from.row, from.col)] || null;
  let next = applyFillToSelection(state, toSel, fill);
  next = applyAlignToSelection(next, toSel, align || 'left');
  const walls = { ...(next.walls || {}) };
  const r0 = Math.min(toSel.r0, toSel.r1);
  const r1 = Math.max(toSel.r0, toSel.r1);
  const c0 = Math.min(toSel.c0, toSel.c1);
  const c1 = Math.max(toSel.c0, toSel.c1);
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      const key = cellKey(r, c);
      if (wall) walls[key] = { ...wall };
      else delete walls[key];
    }
  }
  return { ...next, walls };
}

/**
 * Удалить строки [r0..r1] включительно, сдвинуть ячейки вверх,
 * формулы со ссылками ниже сдвигаем.
 */
export function deleteRows(state, r0, r1) {
  const lo = Math.min(r0, r1);
  const hi = Math.max(r0, r1);
  const count = hi - lo + 1;
  if (count <= 0) return state;
  const newRows = Math.max(1, (state.rows || 1) - count);

  const remapSparse = (map, transformValue) => {
    const next = {};
    for (const [k, v] of Object.entries(map || {})) {
      const [rs, cs] = k.split(',');
      const r = Number(rs);
      const c = Number(cs);
      if (r >= lo && r <= hi) continue;
      const nr = r > hi ? r - count : r;
      if (nr < 0 || nr >= newRows) continue;
      const nv = transformValue ? transformValue(v, r, nr) : v;
      if (nv == null || nv === '') continue;
      next[cellKey(nr, c)] = nv;
    }
    return next;
  };

  const moved = remapSparse(state.cells);
  const cells = {};
  for (const [k, v] of Object.entries(moved)) {
    if (typeof v === 'string' && v.startsWith('=')) {
      cells[k] = shiftRefsAfterRowDelete(v, lo, count);
    } else {
      cells[k] = v;
    }
  }

  return {
    ...state,
    rows: newRows,
    cells,
    fills: remapSparse(state.fills),
    walls: remapSparse(state.walls),
    aligns: remapSparse(state.aligns),
  };
}

/**
 * Удалить столбцы [c0..c1] включительно, сдвинуть влево, формулы обновить.
 */
export function deleteCols(state, c0, c1) {
  const lo = Math.min(c0, c1);
  const hi = Math.max(c0, c1);
  const count = hi - lo + 1;
  if (count <= 0) return state;
  const newCols = Math.max(1, (state.cols || 1) - count);

  const remapSparse = (map) => {
    const next = {};
    for (const [k, v] of Object.entries(map || {})) {
      const [rs, cs] = k.split(',');
      const r = Number(rs);
      const c = Number(cs);
      if (c >= lo && c <= hi) continue;
      const nc = c > hi ? c - count : c;
      if (nc < 0 || nc >= newCols) continue;
      next[cellKey(r, nc)] = v;
    }
    return next;
  };

  const moved = remapSparse(state.cells);
  const cells = {};
  for (const [k, v] of Object.entries(moved)) {
    if (typeof v === 'string' && v.startsWith('=')) {
      cells[k] = shiftRefsAfterColDelete(v, lo, count);
    } else {
      cells[k] = v;
    }
  }

  const colWidths = {};
  for (const [k, v] of Object.entries(state.colWidths || {})) {
    const c = Number(k);
    if (!Number.isFinite(c)) continue;
    if (c >= lo && c <= hi) continue;
    const nc = c > hi ? c - count : c;
    if (nc >= 0 && nc < newCols) colWidths[String(nc)] = v;
  }

  const filter = { ...(state.filter || { enabled: false, columns: {} }) };
  const nextColumns = {};
  for (const [k, v] of Object.entries(filter.columns || {})) {
    const c = Number(k);
    if (!Number.isFinite(c)) continue;
    if (c >= lo && c <= hi) continue;
    const nc = c > hi ? c - count : c;
    if (nc >= 0 && nc < newCols) nextColumns[String(nc)] = v;
  }
  filter.columns = nextColumns;

  return {
    ...state,
    cols: newCols,
    cells,
    fills: remapSparse(state.fills),
    walls: remapSparse(state.walls),
    aligns: remapSparse(state.aligns),
    colWidths,
    filter,
  };
}

function shiftColIndex(letters, absC, deletedLo, count) {
  let col = 0;
  for (const ch of String(letters).toUpperCase()) col = col * 26 + (ch.charCodeAt(0) - 64);
  col -= 1;
  if (col < 0) return null;
  if (col >= deletedLo && col < deletedLo + count) return { keep: true };
  if (col >= deletedLo + count) return { text: `${absC}${colLabel(col - count)}` };
  return { text: `${absC}${String(letters).toUpperCase()}` };
}

/** Сдвинуть ссылки на столбцы строго правее удалённого блока. */
export function shiftRefsAfterColDelete(formula, deletedLo, count) {
  const raw = String(formula || '');
  if (!raw.startsWith('=')) return raw;
  let body = raw.slice(1);
  body = body.replace(/(\$?)([A-Za-z]+)(\$?)(\d+)/g, (full, absC, letters, absR, digits, offset) => {
    const before = body[offset - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) return full;
    const after = body[offset + full.length];
    if (after && /[A-Za-z]/.test(after)) return full;
    const shifted = shiftColIndex(letters, absC, deletedLo, count);
    if (!shifted || shifted.keep) return full;
    return `${shifted.text}${absR}${digits}`;
  });
  body = body.replace(/(\$?)([A-Za-z]+):(\$?)([A-Za-z]+)(?![0-9A-Za-z])/g, (full, a1, l1, a2, l2, offset) => {
    const before = body[offset - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) return full;
    const s1 = shiftColIndex(l1, a1, deletedLo, count);
    const s2 = shiftColIndex(l2, a2, deletedLo, count);
    if (!s1 || !s2 || s1.keep || s2.keep) return full;
    return `${s1.text}:${s2.text}`;
  });
  return `=${body}`;
}

/** Сдвинуть ссылки на строки строго ниже удалённого блока. */
export function shiftRefsAfterRowDelete(formula, deletedLo, count) {
  const raw = String(formula || '');
  if (!raw.startsWith('=')) return raw;
  const body = raw.slice(1);
  const re = /(\$?)([A-Za-z]+)(\$?)(\d+)/g;
  const next = body.replace(re, (full, absC, letters, absR, digits, offset) => {
    const before = body[offset - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) return full;
    const after = body[offset + full.length];
    if (after && /[A-Za-z]/.test(after)) return full;
    const row1 = Number(digits);
    const row0 = row1 - 1;
    if (row0 >= deletedLo && row0 < deletedLo + count) {
      // ссылка в удалённые строки — оставляем как есть (станет «битой»)
      return full;
    }
    if (row0 >= deletedLo + count) {
      const newRow = row0 - count;
      return `${absC}${letters.toUpperCase()}${absR}${newRow + 1}`;
    }
    return full;
  });
  return `=${next}`;
}

function shiftRefsAfterRowInsert(formula, at, count) {
  const raw = String(formula || '');
  if (!raw.startsWith('=')) return raw;
  const body = raw.slice(1);
  const re = /(\$?)([A-Za-z]+)(\$?)(\d+)/g;
  const next = body.replace(re, (full, absC, letters, absR, digits, offset) => {
    const before = body[offset - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) return full;
    const after = body[offset + full.length];
    if (after && /[A-Za-z]/.test(after)) return full;
    const row0 = Number(digits) - 1;
    if (row0 >= at) {
      return `${absC}${letters.toUpperCase()}${absR}${row0 + count + 1}`;
    }
    return full;
  });
  return `=${next}`;
}

function shiftRefsAfterColInsert(formula, at, count) {
  const raw = String(formula || '');
  if (!raw.startsWith('=')) return raw;
  const body = raw.slice(1);
  const re = /(\$?)([A-Za-z]+)(\$?)(\d+)/g;
  const next = body.replace(re, (full, _absC, _letters, _absR, _digits, offset) => {
    const before = body[offset - 1];
    if (before && /[A-Za-zА-Яа-яЁё_]/.test(before)) return full;
    const after = body[offset + full.length];
    if (after && /[A-Za-z]/.test(after)) return full;
    const ref = parseCellRef(full);
    if (!ref) return full;
    if (ref.col >= at) {
      return `${ref.absCol ? '$' : ''}${colLabel(ref.col + count)}${ref.absRow ? '$' : ''}${ref.row + 1}`;
    }
    return full;
  });
  return `=${next}`;
}

/**
 * Вставить count пустых строк перед индексом at (0-based).
 */
export function insertRows(state, at, count = 1) {
  const n = Math.max(1, Number(count) || 1);
  const atRow = Math.max(0, Math.min(Number(at) || 0, state.rows || 0));
  const newRows = Math.min(MAX_FULL_ROWS > 0 ? 20000 : 20000, (state.rows || 0) + n);

  const remapSparse = (map) => {
    const next = {};
    for (const [k, v] of Object.entries(map || {})) {
      const [rs, cs] = k.split(',');
      const r = Number(rs);
      const c = Number(cs);
      const nr = r >= atRow ? r + n : r;
      if (nr < 0 || nr >= newRows) continue;
      next[cellKey(nr, c)] = v;
    }
    return next;
  };

  const moved = remapSparse(state.cells);
  const cells = {};
  for (const [k, v] of Object.entries(moved)) {
    if (typeof v === 'string' && v.startsWith('=')) {
      cells[k] = shiftRefsAfterRowInsert(v, atRow, n);
    } else {
      cells[k] = v;
    }
  }

  return {
    ...state,
    rows: newRows,
    cells,
    fills: remapSparse(state.fills),
    walls: remapSparse(state.walls),
    aligns: remapSparse(state.aligns),
  };
}

/**
 * Вставить count пустых столбцов перед индексом at (0-based).
 */
export function insertCols(state, at, count = 1) {
  const n = Math.max(1, Number(count) || 1);
  const atCol = Math.max(0, Math.min(Number(at) || 0, state.cols || 0));
  const newCols = Math.min(MAX_FULL_COLS, (state.cols || 0) + n);

  const remapSparse = (map) => {
    const next = {};
    for (const [k, v] of Object.entries(map || {})) {
      const [rs, cs] = k.split(',');
      const r = Number(rs);
      const c = Number(cs);
      const nc = c >= atCol ? c + n : c;
      if (nc < 0 || nc >= newCols) continue;
      next[cellKey(r, nc)] = v;
    }
    return next;
  };

  const moved = remapSparse(state.cells);
  const cells = {};
  for (const [k, v] of Object.entries(moved)) {
    if (typeof v === 'string' && v.startsWith('=')) {
      cells[k] = shiftRefsAfterColInsert(v, atCol, n);
    } else {
      cells[k] = v;
    }
  }

  const colWidths = {};
  for (const [k, v] of Object.entries(state.colWidths || {})) {
    const c = Number(k);
    if (!Number.isFinite(c)) continue;
    const nc = c >= atCol ? c + n : c;
    if (nc >= 0 && nc < newCols) colWidths[String(nc)] = v;
  }

  return {
    ...state,
    cols: newCols,
    cells,
    fills: remapSparse(state.fills),
    walls: remapSparse(state.walls),
    aligns: remapSparse(state.aligns),
    colWidths,
  };
}

/**
 * Протянуть значение/формулу из угла выделения на прямоугольник dest.
 * Источник — активная ячейка (обычно левый верх выделения).
 */
export function fillFormulas(state, source, dest) {
  if (!source || !dest) return state;
  const srcVal = state.cells?.[cellKey(source.row, source.col)] ?? '';
  const updates = [];
  const r0 = Math.min(dest.r0, dest.r1);
  const r1 = Math.max(dest.r0, dest.r1);
  const c0 = Math.min(dest.c0, dest.c1);
  const c1 = Math.max(dest.c0, dest.c1);
  for (let r = r0; r <= r1; r += 1) {
    for (let c = c0; c <= c1; c += 1) {
      if (r === source.row && c === source.col) continue;
      const dRow = r - source.row;
      const dCol = c - source.col;
      let value = srcVal;
      if (typeof srcVal === 'string' && srcVal.startsWith('=')) {
        value = shiftFormulaRefs(srcVal, dRow, dCol);
      }
      updates.push({ row: r, col: c, value });
    }
  }
  return setCellsValues(state, updates);
}

/**
 * До какой строки тянуть fill по двойному клику (как Excel):
 * вниз по соседнему столбцу, пока ячейки непустые.
 * @returns {number|null} конечная строка (включительно) или null
 */
export function findAutoFillDownRow(state, bounds) {
  const b = selectionBounds(bounds);
  if (!b) return null;
  const rows = state.rows || 0;
  const cols = state.cols || 0;
  const candidates = [];
  for (const adj of [b.c0 - 1, b.c1 + 1]) {
    if (adj < 0 || adj >= cols) continue;
    let end = b.r1;
    for (let r = b.r1 + 1; r < rows; r += 1) {
      const raw = state.cells?.[cellKey(r, adj)];
      if (raw == null || String(raw).trim() === '') break;
      end = r;
    }
    if (end > b.r1) candidates.push(end);
  }
  if (!candidates.length) return null;
  return Math.max(...candidates);
}

export function selectionBounds(sel) {
  if (!sel) return null;
  return {
    r0: Math.min(sel.r0, sel.r1),
    r1: Math.max(sel.r0, sel.r1),
    c0: Math.min(sel.c0, sel.c1),
    c1: Math.max(sel.c0, sel.c1),
  };
}

export function isInSelection(sel, r, c) {
  const b = selectionBounds(sel);
  if (!b) return false;
  return r >= b.r0 && r <= b.r1 && c >= b.c0 && c <= b.c1;
}

/**
 * «Муравьи» → Enter: формула (со сдвигом) + заливка во все клетки ranges.
 * Стены и align не трогаем.
 */
export function fillAntAcrossRanges(state, ants, ranges) {
  if (!ants || !ranges?.length) return state;
  const srcRow = ants.origin?.row ?? ants.bounds?.r0 ?? 0;
  const srcCol = ants.origin?.col ?? ants.bounds?.c0 ?? 0;
  const srcVal = ants.value != null
    ? String(ants.value)
    : (state.cells?.[cellKey(srcRow, srcCol)] ?? '');
  const srcFill = ants.fill !== undefined
    ? ants.fill
    : ants.fills !== undefined
      ? ants.fills
      : (state.fills?.[cellKey(srcRow, srcCol)] || null);

  const updates = [];
  const fillKeys = [];
  const seen = new Set();
  for (const raw of ranges) {
    const b = selectionBounds(raw);
    if (!b) continue;
    for (let r = b.r0; r <= b.r1; r += 1) {
      for (let c = b.c0; c <= b.c1; c += 1) {
        const key = cellKey(r, c);
        if (seen.has(key)) continue;
        seen.add(key);
        let value = srcVal;
        if (typeof srcVal === 'string' && srcVal.startsWith('=')) {
          value = shiftFormulaRefs(srcVal, r - srcRow, c - srcCol);
        }
        updates.push({ row: r, col: c, value });
        fillKeys.push({ row: r, col: c });
      }
    }
  }
  let next = setCellsValues(state, updates);
  const fills = { ...(next.fills || {}) };
  for (const { row, col } of fillKeys) {
    const key = cellKey(row, col);
    if (srcFill) fills[key] = srcFill;
    else delete fills[key];
  }
  return { ...next, fills };
}
